import { useMemo } from "react"
import { useOutletContext } from "react-router-dom"

import { getUsDateFromIsoDate } from "@components/Date"
import { TRANSACTION_TYPES, ACTIVE_INVESTMENT_STATUSES } from "@components/Domain"
import getStatistics, {
  OUTGOING_TOTAL_KEY,
  STATISTIC_LABELS_MAP,
} from "@components/Investor/helpers/getStatistics"
import getCapitalInvested from "@components/Investor/helpers/getCapitalInvested"
import { getTransactionTypeLabel } from "@components/Transaction"
import { getFormattedDecimalValue } from "@components/Amount"

const LABEL_FUNDING_DATE = "Project Funding Date"

const TOOLTIP_MAP = {
  [OUTGOING_TOTAL_KEY]: "Total amount invested",
  [TRANSACTION_TYPES.SALE_PROFIT]: "Total profit amount upon sale",
  [TRANSACTION_TYPES.CAPITAL_RETURN]: "Total amount returned upon sale or refinance; return of capital",
}


const getActiveInvestments = (investments, projectId) => {
  const activeInvestments = investments
    .filter(({ status }) => ACTIVE_INVESTMENT_STATUSES.includes(status))

  if (!projectId) {
    return activeInvestments
  }

  const projectActiveInvestments = activeInvestments
    .filter(investment => investment.projectId === projectId)

  return projectActiveInvestments
}


const getTransactionStatistics = (activeInvestments, transactions) => {
  const activeInvestmentIds = activeInvestments
    .map(({ id }) => id)

  const activeInvestmentTransactions = transactions
    .filter(({ investmentId }) => activeInvestmentIds.includes(investmentId))

  const statistics = getStatistics(activeInvestmentTransactions)

  return statistics
}


const useStatisticItems = () => {
  const { project, investments, transactions } = useOutletContext()

  const activeInvestments = useMemo(() =>
    getActiveInvestments(investments, project?.id)
  , [ investments, project ])

  const statistics = useMemo(() =>
    getTransactionStatistics(activeInvestments, transactions)
  , [ activeInvestments, transactions ])

  // The capital invested comes from the investments, not from the funding
  // transactions, because a transferred investment keeps its original
  // funding transactions and carries the converted amount in receivedAmount.
  const investedAmount = useMemo(() =>
    getCapitalInvested(activeInvestments)
  , [ activeInvestments ])

  const fundingDate = project?.closeDate

  const fundingUsDate = fundingDate
    ? getUsDateFromIsoDate(fundingDate)
    : "-"

  const amountReturn = statistics[TRANSACTION_TYPES.INVESTMENT_RETURN] || 0
  const profitOnSaleAmount = statistics[TRANSACTION_TYPES.SALE_PROFIT] || 0
  const returnedCapitalAmount = statistics[TRANSACTION_TYPES.CAPITAL_RETURN] || 0

  const returnedAmount = getFormattedDecimalValue(returnedCapitalAmount, profitOnSaleAmount, "plus")

  const items = [
    {
      title: STATISTIC_LABELS_MAP[OUTGOING_TOTAL_KEY],
      value: investedAmount,
      tooltip: TOOLTIP_MAP[OUTGOING_TOTAL_KEY]
    },
    {
      title: "Return on Investment",
      value: amountReturn,
      tooltip: "Total return on investment"
    },
    {
      title: "Return of Capital",
      value: returnedCapitalAmount,
      tooltip: "Total return of capital"
    },
    {
      title: "Total Proceeds",
      value: returnedAmount,
      tooltip: TOOLTIP_MAP[TRANSACTION_TYPES.CAPITAL_RETURN]
    },
    {
      title: getTransactionTypeLabel(TRANSACTION_TYPES.SALE_PROFIT),
      value: profitOnSaleAmount,
      tooltip: TOOLTIP_MAP[TRANSACTION_TYPES.SALE_PROFIT]
    }
  ]

  if (!fundingDate) {
    return items
  }

  items.push({
    title: LABEL_FUNDING_DATE,
    value: fundingUsDate,
    props: {}
  })

  return items
}

export default useStatisticItems
